import React from 'react';
import { PieChart, Plus, Activity, Layers, Zap } from 'lucide-react';
import PageHeader, { PrimaryButton } from 'components/ui/PageHeader';

/* ══════════════════════════════════════════════════════════════════════════
   Audiences page header.

   This file carried a private `KpiCard` — a fourth independent implementation
   of the same metric tile, with its own left accent stripe, its own responsive
   rules and its own `animate-pulse` skeleton that had no dark-mode variant.
   It now uses the shared stat cards via PageHeader's `kpis`, so the metric row
   is identical to every other page's.

   The one behaviour worth keeping from the old card was the sub-line under
   each value ("82% activation rate"). That is now the shared StatCard's
   `hint`, which shows on every breakpoint rather than being hidden on phones.
   ══════════════════════════════════════════════════════════════════════════ */

const AudienceHeader = ({ totalAudiences, activeCount, onCreateSegment, isLoading }) => {
    const total = totalAudiences ?? 0;
    const active = activeCount ?? 0;
    const inactiveCount = total - active;
    const activeRate = total > 0 ? Math.round((active / total) * 100) : 0;

    const kpis = [
        {
            icon: Layers,
            value: totalAudiences,
            label: 'Total Segments',
            hint: 'All audience groups',
            tone: 'brand' as const,
        },
        {
            icon: Activity,
            value: activeCount,
            label: 'Active Segments',
            hint: `${activeRate}% activation rate`,
            tone: 'success' as const,
        },
        {
            icon: Zap,
            value: isLoading ? null : inactiveCount,
            label: 'Inactive',
            hint: 'Paused or draft groups',
            tone: 'violet' as const,
        },
    ];

    return (
        <PageHeader
            icon={<PieChart className="w-5 h-5" />}
            tone="brand"
            title="Audience Segments"
            subtitle="Manage and target your audience groups"
            isLoading={isLoading}
            kpis={kpis}
            actions={
                <PrimaryButton icon={<Plus className="w-4 h-4" />} onClick={onCreateSegment}>
                    Create Segment
                </PrimaryButton>
            }
        />
    );
};

export default AudienceHeader;
